import React, { ReactNode, useEffect, useRef } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { getPermissionDeniedReason } from '@/features/access-control/types/role';
import { canCurrentUser } from '@/lib/auth/currentPermissions';
import useEditorAutoSave from './Hooks/useEditorAutoSave';
import useQueryExecution from './Hooks/useQueryExecution';
import emitter, { type EventListener } from './emitter';
import { handleQEUpdate, setContinueOnErrorFlag } from './reducers/editor/actions';
import { queryEditorStore } from './store';
import { generateUUID } from './utils';

// Inner component that sets up the listener (needs to be inside Provider)
const QueryExecutionListener: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { executeQueries, abortQuery: abortQueryHTTP } = useQueryExecution();
  const dispatch = useDispatch();

  // Hydrate user-level execution preferences once, at the app level. Doing it
  // here rather than per editor keeps every open query sheet on one value —
  // toggling in one sheet is visible in all of them immediately.
  useEffect(() => {
    let cancelled = false;
    void import('./config/executionPrefs')
      .then(({ loadContinueOnError }) => loadContinueOnError())
      .then(({ value }) => {
        if (!cancelled && value) dispatch(setContinueOnErrorFlag(value));
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Auto-save worker — persists across route changes
  useEditorAutoSave();
  console.debug('[QueryEditorProvider] Auto-save worker initialized');

  // Use refs to always access the latest function without re-registering listener
  const executeQueriesRef = useRef(executeQueries);
  const abortQueryRef = useRef(abortQueryHTTP);

  // Keep refs updated with latest functions
  useEffect(() => {
    executeQueriesRef.current = executeQueries;
    abortQueryRef.current = abortQueryHTTP;
  }, [executeQueries, abortQueryHTTP]);

  // HTTP-based query execution listener - only register once on mount
  useEffect(() => {
    const handleQueryExecution = async (data: Record<string, unknown>) => {
      const traceID = generateUUID();
      const modData = { ...data, traceID } as Record<string, unknown>;

      console.log('HTTP sendQE received:', modData, 'traceId:', traceID);

      if (data?.et === 'QUERY') {
        // Permission chokepoint: every execution path funnels through this
        // listener (Run button, Cmd+Enter — which bypasses the button's
        // disabled prop — schema-tree preview auto-run, resume-and-query),
        // so this single check is what makes queryeditor:execute airtight in
        // the UI. Uses the module-level permission mirror because this
        // listener mounts above AuthProvider. ABORT_QUERY stays ungated: a
        // user who lost the permission mid-flight must still be able to stop
        // an already-running query.
        if (!canCurrentUser('queryeditor', 'execute')) {
          const refId = (data.p as { ref_id?: string } | undefined)?.ref_id;
          console.warn('sendQE QUERY blocked: caller lacks queryeditor:execute');
          if (refId) {
            dispatch(
              handleQEUpdate(
                'queriesState',
                {
                  isLoading: false,
                  isRunning: false,
                  processError: getPermissionDeniedReason('queryeditor', 'execute'),
                  processMsg: '',
                  queryCounter: '',
                },
                refId
              ) as unknown as Parameters<typeof dispatch>[0]
            );
          }
          return;
        }
        console.log('Starting HTTP query execution with data:', modData);
        try {
          await executeQueriesRef.current(
            modData as unknown as Parameters<typeof executeQueriesRef.current>[0]
          );
          console.log('HTTP query execution completed successfully');
        } catch (error) {
          console.error('HTTP query execution failed:', error);
          if (error instanceof Error) {
            console.error('Error details:', error.stack);
          }
        }
      } else if (data?.et === 'ABORT_QUERY') {
        try {
          await abortQueryRef.current(
            modData as unknown as Parameters<typeof abortQueryRef.current>[0]
          );
        } catch (error) {
          console.error('HTTP query abort failed:', error);
        }
      }
    };

    console.log('Registering sendQE listener at app level');
    emitter.on('sendQE', handleQueryExecution as EventListener);

    return () => {
      console.log('Removing sendQE listener');
      emitter.removeListener('sendQE', handleQueryExecution as EventListener);
    };
  }, [dispatch]); // dispatch is referentially stable — still mount/unmount only

  return <>{children}</>;
};

interface QueryEditorProviderProps {
  children: ReactNode;
}

/**
 * QueryEditorProvider - Provides Redux store and query execution listener at app level
 * This ensures the listener persists across route changes
 */
export const QueryEditorProvider: React.FC<QueryEditorProviderProps> = ({ children }) => {
  return (
    <Provider store={queryEditorStore}>
      <QueryExecutionListener>{children}</QueryExecutionListener>
    </Provider>
  );
};

export default QueryEditorProvider;
